import { useTranslation } from 'react-i18next'
import { ComponentProps, ReactNode, useCallback, useMemo } from 'react'
import { useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { CogsSelectableKeys, DirectCostUnitCost, DirectCostUnitCostConfig } from '../api/types'
import { useGetDataGroupedByYear } from '@/lib/hooks/useGroupData'
import { usePageContext } from '@/lib/context/page'
import { TableErrorProvider } from '@/feature/financial-model/ecommerce/revenues/context/TableErrorContext'
import { useCogsStore } from '../store'
import { Table, TableBody } from '@/components/ui/table'
import { useFormatLocalizedNumber } from '@/lib/hooks/useLocaleFormatter'
import { BadgesByScenario, CogsBadge, CogsBadgeOptions } from './CogsBadges'
import {
  directCostsPeriodicDataQueryOptions,
  postDirectCostsPeriodicData,
  postDirectCostsPeriodicDataBulk,
  useGetCogsDirectCostsCategories,
  useGetDirectCostsPeriodicData,
  useSuspenseUnitsSettings
} from '../api'
import { Container } from '@/components/shared/Container'
import { CogsInputCell } from './direct-costs-input-cells.tsx'
import { GenericPeriodTableHeader, InputTypeProviderProvider, PeriodTableRow } from '@/components/shared/tables'
import { PeriodType } from '@/types'
import { Accordion, AccordionContent, AccordionTrigger, AccordionItem } from '@/components/ui/accordion'
import { NavigationProvider, useNavigationContext, YearNavigation } from '@/lib/context/navigation'
import { COGS_BASE_KEY, createQueryKey, TOTALS_KEY } from '@/api/query-keys.ts'
import { ActionablePeriodRowCell, ActionableRowFeedbackDialog, TableActionableRowProvider } from '@/components/shared/tables/actionable-row.tsx'
import { Show } from '@/components/shared/show'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore'
import { TableContextValue, TableProvider } from '@/components/shared/tables/table-context.tsx'
import { PeriodCellContent, PeriodFixedTableCell, PeriodRowHeader, PeriodTableCell } from '@/components/shared/tables/table-cell.tsx'
import ApplyRulesButton from '@/components/shared/tables/ApplyRulesButton'
import { HistoricalValue, openForecastRulePanel } from '@/lib/sidePanelEmitter'

const useRulesPanel = ({
  periods,
  categoryId,
  itemName,
  financialModelId,
  productId
}: {
  periods: DirectCostUnitCost[]
  categoryId?: string
  itemName?: string
  financialModelId: string
  productId?: string | null
}) => {
  const { state } = usePageContext()
  const queryClient = useQueryClient()

  return useCallback(
    async (mutateDataKey: CogsSelectableKeys, rowName: string) => {
      if (!financialModelId || !periods.length) return

      const historicalDirectCostData = await queryClient.ensureQueryData(
        directCostsPeriodicDataQueryOptions({
          financialModelId,
          period_type: `historical`,
          view: state.view,
          categoryId,
          productId: productId ?? undefined
        })
      )

      const historicalValues: HistoricalValue[] = historicalDirectCostData
        .flatMap(entry => entry.periods)
        .map(period => ({
          fiscal_year: period.fiscal_year,
          fiscal_month: period.fiscal_month,
          period: period.period,
          period_type: period.period_type,
          direct_cost: period.direct_cost,
          cost_per_unit: period.cost_per_unit,
          percentage_of_revs: period.percentage_of_revs,
          revenue: period.revenue,
          average_price_per_unit: period.average_price_per_unit,
          total_applicable_units: period.total_applicable_units,
          percentage_of_units: period.percentage_of_units
        }))

      openForecastRulePanel({
        itemName: itemName ?? periods[0].product_name ?? undefined,
        financialModelId,
        queryPeriod: state.queryPeriod,
        view: state.view,
        rowName,
        mutateDataKey,
        directCostPeriods: periods,
        historicalValues,
        directCostCategoryId: categoryId ?? null
      })
    },
    [categoryId, financialModelId, itemName, periods, productId, queryClient, state.queryPeriod, state.view]
  )
}

const SCENARIOS_MAP = {
  '1_aggregate_absolute': 1,
  '2_aggregate_cost_per_unit': 2,
  '4_aggregate_percentage_of_revenues': 4,
  '3_byproduct': 3
} as const

const useCogsInputRowActionableContextProps = ({
  currentData,
  mutateDataKey,
  financialModelId
}: {
  currentData: DirectCostUnitCost[]
  financialModelId: string
  mutateDataKey: CogsSelectableKeys
}) => {
  const queryClient = useQueryClient()

  return useMemo(
    () =>
      ({
        currentData,
        mutateDataKey,
        rowUpdateMutationArgs: {
          mutationKey: createQueryKey({
            baseKey: COGS_BASE_KEY,
            keys: [`direct-costs`, financialModelId]
          }),
          mutationItemFn: async (item: DirectCostUnitCost, { rowType }) => {
            await postDirectCostsPeriodicData({
              financialModelId,
              data: {
                ...item,
                config: {
                  ...item.config,
                  [mutateDataKey]: {
                    type: rowType,
                    allowed_types: item.config[mutateDataKey]?.allowed_types ?? []
                  }
                }
              }
            })

            queryClient.invalidateQueries({
              queryKey: createQueryKey({ baseKey: COGS_BASE_KEY, keys: [`direct-costs`, financialModelId] })
            })

            queryClient.resetQueries({
              queryKey: createQueryKey({ baseKey: TOTALS_KEY, keys: [financialModelId, `direct-costs`] })
            })
          },
          mutationBulkFn: async (items: DirectCostUnitCost[], { rowType }) => {
            const payloads = items.map((item, itemIndex) => {
              const base = currentData[itemIndex]

              return {
                ...(base.id ? { id: base.id } : {}),
                ...base,
                config: {
                  ...base.config,
                  [mutateDataKey]: {
                    type: rowType,
                    allowed_types: base.config[mutateDataKey]?.allowed_types ?? []
                  }
                },
                [mutateDataKey]: item[mutateDataKey]
              }
            })

            await postDirectCostsPeriodicDataBulk({ financialModelId, data: payloads })

            queryClient.invalidateQueries({
              queryKey: createQueryKey({ baseKey: COGS_BASE_KEY, keys: [`direct-costs`, financialModelId] })
            })

            queryClient.resetQueries({
              queryKey: createQueryKey({ baseKey: TOTALS_KEY, keys: [financialModelId, `direct-costs`] })
            })
          }
        }
      }) as Omit<ComponentProps<typeof TableActionableRowProvider<DirectCostUnitCost>>, 'children'>,
    [currentData, mutateDataKey, financialModelId, queryClient]
  )
}

const CopyAndPasteRow = ({
  financialModelId,
  periods,
  selectedKey,
  children
}: {
  financialModelId: string
  periods: DirectCostUnitCost[]
  selectedKey: CogsSelectableKeys
  children: ReactNode
}) => {
  const actionableRowContextProps = useCogsInputRowActionableContextProps({
    currentData: periods,
    financialModelId,
    mutateDataKey: selectedKey
  })

  return (
    <TableActionableRowProvider {...actionableRowContextProps}>
      <ActionableRowFeedbackDialog>{children}</ActionableRowFeedbackDialog>
    </TableActionableRowProvider>
  )
}

const HistoricalScenarioRows = ({ periods }: { periods: DirectCostUnitCost[] }) => {
  const { t } = useTranslation(`cogs`)
  const financialModelId = periods[0].financial_model_id
  const financialModelSettings = useFinancialModelSettingsStore(settings => settings.settings[financialModelId!])
  const modelingApproach = useMemo(() => periods[0].modeling_approach, [periods])

  const formatNumber = useFormatLocalizedNumber({
    style: `currency`,
    currency: financialModelSettings?.currency?.functionalCurrency
  })

  const { state } = usePageContext()

  switch (modelingApproach) {
    case `absolute_value`:
      return (
        <PeriodTableRow type='output'>
          <PeriodFixedTableCell>
            <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.revenues`)}</PeriodRowHeader>
          </PeriodFixedTableCell>
          {periods.map(period => (
            <PeriodTableCell key={period.period} cell_period={period.period}>
              <PeriodCellContent>{formatNumber(period.revenue) ?? `--`}</PeriodCellContent>
            </PeriodTableCell>
          ))}
        </PeriodTableRow>
      )
    case `cost_per_unit`:
      return (
        <>
          <PeriodTableRow type='output'>
            <PeriodFixedTableCell>
              <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.total_units`)}</PeriodRowHeader>
            </PeriodFixedTableCell>
            {periods.map(period => (
              <PeriodTableCell key={period.period} cell_period={period.period}>
                <PeriodCellContent>
                  {formatNumber(period.total_applicable_units, {
                    style: `decimal`,
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0
                  }) ?? `--`}
                </PeriodCellContent>
              </PeriodTableCell>
            ))}
          </PeriodTableRow>
          <PeriodTableRow type='output'>
            <PeriodFixedTableCell>
              <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.average_price_per_unit`)}</PeriodRowHeader>
            </PeriodFixedTableCell>
            {periods.map(period => (
              <PeriodTableCell key={period.period} cell_period={period.period}>
                <PeriodCellContent>{formatNumber(period.average_price_per_unit) ?? `--`}</PeriodCellContent>
              </PeriodTableCell>
            ))}
          </PeriodTableRow>
          <PeriodTableRow type={state.view === `yearly` ? `output` : `input`}>
            <PeriodFixedTableCell>
              <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.cost_per_unit`)}</PeriodRowHeader>
            </PeriodFixedTableCell>
            <CopyAndPasteRow financialModelId={financialModelId} periods={periods} selectedKey='cost_per_unit'>
              {periods.map((period, index) => (
                <ActionablePeriodRowCell key={period.period} cellIndex={index} cell_period={period.period}>
                  <PeriodCellContent>
                    <CogsInputCell key={period.cost_per_unit ?? 0} period={period} selectedKey='cost_per_unit' />
                  </PeriodCellContent>
                </ActionablePeriodRowCell>
              ))}
            </CopyAndPasteRow>
          </PeriodTableRow>
        </>
      )
    case `percentage_of_revenues`:
      return (
        <>
          <PeriodTableRow type='output'>
            <PeriodFixedTableCell>
              <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.revenues`)}</PeriodRowHeader>
            </PeriodFixedTableCell>
            {periods.map(period => (
              <PeriodTableCell key={period.period} cell_period={period.period}>
                <PeriodCellContent>{formatNumber(period.revenue) ?? `--`}</PeriodCellContent>
              </PeriodTableCell>
            ))}
          </PeriodTableRow>
          <PeriodTableRow type={state.view === `yearly` ? `output` : `input`}>
            <PeriodFixedTableCell>
              <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.percentage_of_revenues`)}</PeriodRowHeader>
            </PeriodFixedTableCell>
            <CopyAndPasteRow financialModelId={financialModelId} periods={periods} selectedKey='percentage_of_revs'>
              {periods.map((period, index) => (
                <ActionablePeriodRowCell key={period.period} cellIndex={index} cell_period={period.period}>
                  <PeriodCellContent>
                    <CogsInputCell key={period.percentage_revenues ?? 0} period={period} selectedKey='percentage_of_revs' />
                  </PeriodCellContent>
                </ActionablePeriodRowCell>
              ))}
            </CopyAndPasteRow>
          </PeriodTableRow>
        </>
      )
    default:
      return null
  }
}

const ForecastScenarioRows = ({
  periods,
  config,
  categoryId,
  itemName
}: {
  periods: DirectCostUnitCost[]
  config: DirectCostUnitCostConfig
  categoryId?: string
  itemName?: string
}) => {
  const { t } = useTranslation(`cogs`)
  const { state } = usePageContext()
  const { id: financialModelId } = useParams<{ id: string }>()

  const openRulesPanel = useRulesPanel({
    periods,
    categoryId,
    itemName,
    financialModelId: financialModelId!,
    productId: periods[0]?.product_id ?? undefined
  })

  const financialModelSettings = useFinancialModelSettingsStore(settings => settings.settings[financialModelId!])
  const modelingApproach = useMemo(() => periods[0].modeling_approach, [periods])

  const formatNumber = useFormatLocalizedNumber({
    style: `currency`,
    currency: financialModelSettings?.currency?.functionalCurrency ?? `EUR`
  })

  const rowNames = useMemo(
    () => ({
      costPerUnit: t(`cogs.periods.direct_costs.table.rows.cost_per_unit`),
      percentageOfRevenues: t(`cogs.periods.direct_costs.table.rows.percentage_of_revenues`)
    }),
    [t]
  )

  const isButtonApplyRulesVisible = state.view === `monthly`

  switch (modelingApproach) {
    case `absolute_value`:
      return (
        <InputTypeProviderProvider defaultType={`absolute`}>
          <PeriodTableRow type='output'>
            <PeriodFixedTableCell>
              <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.revenues`)}</PeriodRowHeader>
            </PeriodFixedTableCell>
            {periods.map(period => (
              <PeriodTableCell
                key={period.period}
                cell_period={period.period}
                readonly={state.queryPeriod === `all` && state.period !== period.period_type}
              >
                <PeriodCellContent>{formatNumber(period.revenue) ?? `--`}</PeriodCellContent>
              </PeriodTableCell>
            ))}
          </PeriodTableRow>
        </InputTypeProviderProvider>
      )
    case `cost_per_unit`:
      return (
        <>
          <InputTypeProviderProvider defaultType={`absolute`}>
            <PeriodTableRow type='output'>
              <PeriodFixedTableCell>
                <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.total_units`)}</PeriodRowHeader>
              </PeriodFixedTableCell>
              {periods.map(period => (
                <PeriodTableCell
                  key={period.period}
                  cell_period={period.period}
                  readonly={state.queryPeriod === `all` && state.period !== period.period_type}
                >
                  <PeriodCellContent>
                    {formatNumber(period.total_applicable_units, {
                      style: `decimal`,
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0
                    }) ?? `--`}
                  </PeriodCellContent>
                </PeriodTableCell>
              ))}
            </PeriodTableRow>
          </InputTypeProviderProvider>
          <InputTypeProviderProvider defaultType={`absolute`}>
            <PeriodTableRow type='output'>
              <PeriodFixedTableCell>
                <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.average_price_per_unit`)}</PeriodRowHeader>
              </PeriodFixedTableCell>
              {periods.map(period => (
                <PeriodTableCell
                  key={period.period}
                  cell_period={period.period}
                  readonly={state.queryPeriod === `all` && state.period !== period.period_type}
                >
                  <PeriodCellContent type={`output`}>{formatNumber(period.average_price_per_unit) ?? `--`}</PeriodCellContent>
                </PeriodTableCell>
              ))}
            </PeriodTableRow>
          </InputTypeProviderProvider>
          <InputTypeProviderProvider defaultType={config.cost_per_unit.type}>
            <PeriodTableRow type={state.view === `yearly` ? `output` : `input`}>
              <PeriodFixedTableCell>
                <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.cost_per_unit`)}</PeriodRowHeader>
                {isButtonApplyRulesVisible && <ApplyRulesButton onClick={() => openRulesPanel(`cost_per_unit`, rowNames.costPerUnit)} />}
              </PeriodFixedTableCell>
              <CopyAndPasteRow financialModelId={financialModelId!} periods={periods} selectedKey='cost_per_unit'>
                {periods.map((period, index) => (
                  <ActionablePeriodRowCell
                    key={period.period}
                    cellIndex={index}
                    cell_period={period.period}
                    readonly={state.queryPeriod === `all` && state.period !== period.period_type}
                  >
                    <PeriodCellContent
                      type={(state.queryPeriod === `all` && state.period !== period.period_type) || state.view === `yearly` ? `output` : `input`}
                    >
                      <CogsInputCell period={period} selectedKey='cost_per_unit' />
                    </PeriodCellContent>
                  </ActionablePeriodRowCell>
                ))}
              </CopyAndPasteRow>
            </PeriodTableRow>
          </InputTypeProviderProvider>
        </>
      )
    case `percentage_of_revenues`:
      return (
        <>
          <InputTypeProviderProvider defaultType={`absolute`}>
            <PeriodTableRow type='output'>
              <PeriodFixedTableCell>
                <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.revenues`)}</PeriodRowHeader>
              </PeriodFixedTableCell>
              {periods.map(period => (
                <PeriodTableCell
                  key={period.period}
                  cell_period={period.period}
                  readonly={state.queryPeriod === `all` && state.period !== period.period_type}
                >
                  <PeriodCellContent>{formatNumber(period.revenue) ?? `--`}</PeriodCellContent>
                </PeriodTableCell>
              ))}
            </PeriodTableRow>
          </InputTypeProviderProvider>
          <InputTypeProviderProvider defaultType={config.percentage_of_revs.type}>
            <PeriodTableRow type={state.view === `yearly` ? `output` : `input`}>
              <PeriodFixedTableCell>
                <PeriodRowHeader>{t(`cogs.periods.direct_costs.table.rows.percentage_of_revenues`)}</PeriodRowHeader>
                {isButtonApplyRulesVisible && (
                  <ApplyRulesButton onClick={() => openRulesPanel(`percentage_of_revs`, rowNames.percentageOfRevenues)} />
                )}
              </PeriodFixedTableCell>
              <CopyAndPasteRow financialModelId={financialModelId!} periods={periods} selectedKey='percentage_of_revs'>
                {periods.map((period, index) => (
                  <ActionablePeriodRowCell
                    key={period.period}
                    cellIndex={index}
                    cell_period={period.period}
                    readonly={state.queryPeriod === `all` && state.period !== period.period_type}
                  >
                    <PeriodCellContent
                      type={(state.queryPeriod === `all` && state.period !== period.period_type) || state.view === `yearly` ? `output` : `input`}
                    >
                      <CogsInputCell period={period} selectedKey='percentage_of_revs' />
                    </PeriodCellContent>
                  </ActionablePeriodRowCell>
                ))}
              </CopyAndPasteRow>
            </PeriodTableRow>
          </InputTypeProviderProvider>
        </>
      )
    default:
      return null
  }
}

const ScenarioRows = ({
  periods,
  config,
  categoryId,
  itemName
}: {
  periods: DirectCostUnitCost[]
  config?: DirectCostUnitCostConfig
  categoryId?: string
  itemName?: string
}) => {
  const { period } = useParams<{ period: PeriodType }>()

  switch (period) {
    case `historical`:
      return <HistoricalScenarioRows periods={periods} />
    case `forecast`:
      return <ForecastScenarioRows periods={periods} config={config!} categoryId={categoryId} itemName={itemName} />
    default:
      return null
  }
}

export const HistoricalDataTable = ({
  periods,
  categoryId,
  variant
}: {
  periods: DirectCostUnitCost[]
  categoryId?: string
  variant: 'base' | 'accordion-item'
}) => {
  const { t } = useTranslation(`cogs`)
  const financialModelId = periods[0].financial_model_id
  const { years, months, availableYears, lastHistoricalPeriod } = useGetDataGroupedByYear<DirectCostUnitCost>(periods)
  const { state } = usePageContext()
  const { tableRef } = useNavigationContext()
  const modelingApproach = periods[0].modeling_approach
  const { data: categoryUnitSettings } = useSuspenseUnitsSettings(financialModelId!, categoryId)
  const {
    data: { categories }
  } = useGetCogsDirectCostsCategories(financialModelId)

  const fallbackProductName = periods[0].product_name ?? ``
  const contentTitle =
    categoryId && variant === `base` ? (categories.find(c => c.id === categoryId)?.name ?? `Unnamed Category`) : fallbackProductName

  const financialModelSettings = useFinancialModelSettingsStore(settings => settings.settings[financialModelId!])
  const formatLocalizedNumber = useFormatLocalizedNumber({
    style: `currency`,
    currency: financialModelSettings?.currency?.functionalCurrency
  })

  const ItemContent = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionItem
    }
    return Container
  }, [variant])

  const Trigger = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionTrigger
    }
    return `div`
  }, [variant])

  const Content = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionContent
    }
    return `div`
  }, [variant])

  const tableContext = useMemo(
    () =>
      ({
        lastHistoricalPeriod,
        view: state.view,
        queryPeriod: state.queryPeriod,
        period: state.period
      }) satisfies TableContextValue,
    [lastHistoricalPeriod, state.view, state.queryPeriod, state.period]
  )
  return (
    <div className='mt-4 flex flex-col gap-6'>
      <ItemContent value={String(periods[0].product_id!)} className='rounded-lg bg-white px-4 shadow-loose'>
        <Trigger className='group hover:no-underline'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-medium transition-all duration-300 group-hover:underline'>{contentTitle}</h3>
            <Show when={!!categoryId && !!categoryUnitSettings.level_of_granularity}>
              <CogsBadge type={categoryUnitSettings.level_of_granularity!} size='md' variant='info' />
              {modelingApproach && <CogsBadge size='md' variant='info' type={modelingApproach as CogsBadgeOptions} />}
            </Show>
            <Show when={!categoryId && variant === `accordion-item`}>
              <CogsBadge size='md' variant='info' type={modelingApproach as CogsBadgeOptions} />
            </Show>
          </div>
        </Trigger>
        <Content>
          <TableProvider value={tableContext}>
            <TableErrorProvider>
              <YearNavigation view={state.view} years={availableYears} className='mt-6' />
              <Table className='mt-6 overflow-auto' ref={tableRef}>
                <GenericPeriodTableHeader view={state.view} years={years} months={months} />
                <TableBody>
                  <ScenarioRows periods={periods} categoryId={categoryId} itemName={contentTitle} />
                  <PeriodTableRow type={state.view === `yearly` || modelingApproach !== `absolute_value` ? `totals` : `total-input`}>
                    <PeriodFixedTableCell>
                      <PeriodRowHeader direction='horizontal'>{t(`cogs.periods.direct_costs.table.rows.direct_costs`)}</PeriodRowHeader>
                    </PeriodFixedTableCell>
                    <Show
                      when={modelingApproach === `absolute_value`}
                      fallback={periods.map(period => (
                        <PeriodTableCell key={period.period} cell_period={period.period}>
                          <PeriodCellContent type={`output`}>
                            <span>{formatLocalizedNumber(period.direct_cost) ?? `--`}</span>
                          </PeriodCellContent>
                        </PeriodTableCell>
                      ))}
                    >
                      <CopyAndPasteRow financialModelId={financialModelId} periods={periods} selectedKey='direct_cost'>
                        {periods.map((period, index) => (
                          <ActionablePeriodRowCell
                            key={period.period}
                            cellIndex={index}
                            cell_period={period.period}
                            readonly={state.queryPeriod === `all` && state.period !== period.period_type}
                          >
                            <PeriodCellContent
                              type={
                                (state.queryPeriod === `all` && state.period !== period.period_type) || state.view === `yearly` ? `output` : `input`
                              }
                            >
                              <CogsInputCell key={period.direct_cost ?? 0} period={period} selectedKey='direct_cost' categoryId={categoryId} />
                            </PeriodCellContent>
                          </ActionablePeriodRowCell>
                        ))}
                      </CopyAndPasteRow>
                    </Show>
                  </PeriodTableRow>
                </TableBody>
              </Table>
            </TableErrorProvider>
          </TableProvider>
        </Content>
      </ItemContent>
    </div>
  )
}

export const ForecastDataTable = ({
  periods,
  config,
  categoryId,
  variant
}: {
  periods: DirectCostUnitCost[]
  config: DirectCostUnitCostConfig
  categoryId?: string
  variant: 'base' | 'accordion-item'
}) => {
  const { id: financialModelId } = useParams<{ id: string }>()
  const { t } = useTranslation(`cogs`)
  const { years, months, lastHistoricalPeriod, availableYears } = useGetDataGroupedByYear<DirectCostUnitCost>(periods)
  const { state } = usePageContext()
  const { tableRef } = useNavigationContext()
  const productID = periods[0].product_id
  const modelingApproach = periods[0].modeling_approach
  const { data: categoryUnitSettings } = useSuspenseUnitsSettings(financialModelId!, categoryId)

  const {
    data: { categories }
  } = useGetCogsDirectCostsCategories(financialModelId!)

  const fallbackProductName = periods[0].product_name ?? ``
  const contentTitle =
    categoryId && variant === `base` ? (categories.find(c => c.id === categoryId)?.name ?? `Unnamed Category`) : fallbackProductName

  const openRulesPanel = useRulesPanel({
    periods,
    categoryId,
    itemName: contentTitle,
    financialModelId: financialModelId!,
    productId: periods[0]?.product_id ?? undefined
  })

  const ItemContent = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionItem
    }
    return Container
  }, [variant])

  const Trigger = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionTrigger
    }
    return `div`
  }, [variant])

  const Content = useMemo(() => {
    if (variant === `accordion-item`) {
      return AccordionContent
    }
    return `div`
  }, [variant])

  const financialModelSettings = useFinancialModelSettingsStore(settings => settings.settings[financialModelId!])

  const formatLocalizedNumber = useFormatLocalizedNumber({
    style: `currency`,
    currency: financialModelSettings?.currency?.functionalCurrency
  })

  const tableContext = useMemo(
    () =>
      ({
        lastHistoricalPeriod,
        view: state.view,
        queryPeriod: state.queryPeriod,
        period: state.period
      }) satisfies TableContextValue,
    [lastHistoricalPeriod, state.view, state.queryPeriod, state.period]
  )

  const directCostRowName = t(`cogs.periods.direct_costs.table.rows.direct_costs`)
  const isButtonApplyRulesVisible = state.view === `monthly`

  return (
    <div className='mt-4 flex flex-col gap-6'>
      <ItemContent value={String(productID!)} className='rounded-lg bg-white px-4 shadow-loose'>
        <Trigger>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-medium'>{contentTitle}</h3>
            <Show when={!!categoryId && !!categoryUnitSettings.level_of_granularity}>
              <CogsBadge type={categoryUnitSettings.level_of_granularity!} size='md' variant='info' />
              {modelingApproach && <CogsBadge size='md' variant='info' type={modelingApproach as CogsBadgeOptions} />}
            </Show>
            <Show when={!categoryId && variant === `accordion-item`}>
              <CogsBadge size='md' variant='info' type={modelingApproach as CogsBadgeOptions} />
            </Show>
          </div>
        </Trigger>
        <Content>
          <TableProvider value={tableContext}>
            <TableErrorProvider>
              <YearNavigation view={state.view} years={availableYears} className='mt-6' />
              <Table className='mt-6 overflow-auto' ref={tableRef}>
                <GenericPeriodTableHeader view={state.view} years={years} months={months} />
                <TableBody>
                  <ScenarioRows periods={periods} config={config} categoryId={categoryId} itemName={contentTitle} />
                  <InputTypeProviderProvider defaultType={config.direct_cost.type}>
                    <PeriodTableRow type={state.view === `yearly` || modelingApproach !== `absolute_value` ? `totals` : `total-input`}>
                      <PeriodFixedTableCell>
                        <PeriodRowHeader direction='horizontal' type='output'>
                          {directCostRowName}
                        </PeriodRowHeader>
                        {modelingApproach === `absolute_value` && isButtonApplyRulesVisible && (
                          <ApplyRulesButton onClick={() => openRulesPanel(`direct_cost`, directCostRowName)} />
                        )}
                      </PeriodFixedTableCell>
                      <Show
                        when={modelingApproach === `absolute_value`}
                        fallback={periods.map(period => (
                          <PeriodTableCell key={period.period} cell_period={period.period}>
                            <PeriodCellContent type={`output`}>{formatLocalizedNumber(period.direct_cost) ?? `--`}</PeriodCellContent>
                          </PeriodTableCell>
                        ))}
                      >
                        <CopyAndPasteRow financialModelId={financialModelId!} periods={periods} selectedKey='direct_cost'>
                          {periods.map((period, index) => (
                            <ActionablePeriodRowCell key={period.period} cellIndex={index} cell_period={period.period}>
                              <PeriodCellContent>
                                <CogsInputCell key={period.direct_cost ?? 0} period={period} categoryId={categoryId} selectedKey='direct_cost' />
                              </PeriodCellContent>
                            </ActionablePeriodRowCell>
                          ))}
                        </CopyAndPasteRow>
                      </Show>
                    </PeriodTableRow>
                  </InputTypeProviderProvider>
                </TableBody>
              </Table>
            </TableErrorProvider>
          </TableProvider>
        </Content>
      </ItemContent>
    </div>
  )
}

export const CogsTable = ({ categoryId }: { categoryId?: string }) => {
  const { state } = usePageContext()
  const { id: financialModelId } = useParams<{ id: string }>()

  const { data } = useGetDirectCostsPeriodicData({
    financialModelId: financialModelId!,
    period_type: state.queryPeriod,
    view: state.view,
    categoryId
  })

  const { scenario } = useCogsStore()

  switch (state.period) {
    case `historical`:
      if (scenario === SCENARIOS_MAP[`3_byproduct`]) {
        return (
          <div className=''>
            <BadgesByScenario scenario={scenario} />
            <Accordion type='multiple' defaultValue={data.map(periods => periods?.periods[0]?.product_id!)}>
              {data.map((periods, i) => (
                <NavigationProvider
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                >
                  <HistoricalDataTable periods={periods?.periods ?? []} variant={`accordion-item`} />
                </NavigationProvider>
              ))}
            </Accordion>
          </div>
        )
      }

      return (
        <div className=''>
          <BadgesByScenario scenario={scenario} />
          {data.map((periods, i) => (
            <NavigationProvider
              // eslint-disable-next-line react/no-array-index-key
              key={i}
            >
              <HistoricalDataTable periods={periods?.periods ?? []} variant={`base`} />
            </NavigationProvider>
          ))}
        </div>
      )

    case `forecast`:
      if (scenario === SCENARIOS_MAP[`3_byproduct`]) {
        return (
          <div className=''>
            <BadgesByScenario scenario={scenario} />
            <Accordion type='multiple' defaultValue={data.map(periods => periods?.periods[0]?.product_id!)}>
              {data.map((periods, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <NavigationProvider key={i}>
                  <ForecastDataTable variant={`accordion-item`} periods={periods?.periods ?? []} config={periods?.config ?? {}} />
                </NavigationProvider>
              ))}
            </Accordion>
          </div>
        )
      }

      return (
        <div className=''>
          <BadgesByScenario scenario={scenario} />
          {data.map((periods, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <NavigationProvider key={i}>
              <ForecastDataTable variant={`base`} periods={periods.periods} config={periods.config} />
            </NavigationProvider>
          ))}
        </div>
      )

    default:
      return null
  }
}
